import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './InauguralConfirmationPage.css';

function InauguralConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAppPopup, setShowAppPopup] = useState(false);
  const [daysUntil, setDaysUntil] = useState(null);
  
  // Get data from navigation state
  const { firstName, routeName, startDate, endDate } = location.state || {};

  // Calculate days until journey
  useEffect(() => {
    if (startDate) {
      const start = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      start.setHours(0, 0, 0, 0);
      
      const diffTime = start - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysUntil(diffDays);
    }
  }, [startDate]);

  // Redirect if no data
  useEffect(() => {
    if (!location.state || !firstName) {
      navigate('/');
    }
  }, [location.state, firstName, navigate]);

  const handleHomeClick = () => {
    navigate('/');
  };

  const handlePolaroidClick = (platform) => {
    if (platform === 'ios') {
      // App Store link (will work once app is approved)
      // For now, use TestFlight if you have it set up
      window.open('https://apps.apple.com/app/backpass/id6751730283', '_blank');

      // Alternative: TestFlight link if you have public testing enabled:
      // window.open('https://testflight.apple.com/join/YOUR_CODE', '_blank');
    } else if (platform === 'android') {
      // For Android, you can link to Play Store once published
      // window.open('https://play.google.com/store/apps/details?id=com.backpass.mobile', '_blank');

      // For now, show the popup since Android may not be published yet
      setShowAppPopup(true);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="inaugural-confirmation-page">
      {/* Logo - now clickable */}
      <div className="confirmation-logo-wrapper" onClick={handleHomeClick} style={{ cursor: 'pointer' }}>
        <img src="/images/logodownsized.webp" alt="BackPass Logo" className="confirmation-logo" />
      </div>

      {/* Main Content Container */}
      <div className="confirmation-container">
        <h1 className="confirmation-title">
          You're going on the {routeName || 'thrill of immediately contacting matthew@backpass.co if you see this message lmao. This means your route name is missing.'}, {firstName}!
        </h1>

        {/* Journey Dates Display */}
        {startDate && endDate && (
          <div className="journey-code-display">
            <p className="journey-code-label">Your Adventure Dates:</p>
            <p className="journey-code-value" style={{ fontSize: '24px', letterSpacing: '2px' }}>
              {formatDate(startDate)} - {formatDate(endDate)}
            </p>
            {daysUntil !== null && (
              <p className="journey-code-note">
                {daysUntil > 0 
                  ? `${daysUntil} days until your adventure begins!` 
                  : daysUntil === 0 
                  ? `Your adventure starts today!`
                  : `Your adventure has begun!`
                }
              </p>
            )}
          </div>
        )}

        {/* Polaroid App Links */}
        <div className="app-polaroids">
          <div className="polaroid" onClick={() => handlePolaroidClick('android')}>
            <div className="polaroid-content">
              <div className="app-icon-placeholder">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                  <path d="M17 2H7C5.9 2 5 2.9 5 4V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V4C19 2.9 18.1 2 17 2ZM12 20C10.9 20 10 19.1 10 18C10 16.9 10.9 16 12 16C13.1 16 14 16.9 14 18C14 19.1 13.1 20 12 20ZM17 16H7V6H17V16Z" fill="#072231"/>
                </svg>
              </div>
            </div>
            <div className="polaroid-caption">Download for Android</div>
          </div>

          <div className="polaroid" onClick={() => handlePolaroidClick('ios')}>
            <div className="polaroid-content">
              <div className="app-icon-placeholder">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="#072231"/>
                </svg>
              </div>
            </div>
            <div className="polaroid-caption">Download for iPhone</div>
          </div>
        </div>

        {/* Contact */}
        <p className="contact-info">
          Any questions? Contact <a href="mailto:matthew@backpass.co">matthew@backpass.co</a>
        </p>

        {/* Home Button */}
        <button className="home-button" onClick={handleHomeClick}>
          Back to Home
        </button>
      </div>

      {/* App Not Ready Popup */}
      {showAppPopup && (
        <div className="app-popup-overlay" onClick={() => setShowAppPopup(false)}>
          <div className="app-popup-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="app-popup-close"
              onClick={() => setShowAppPopup(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3>App Coming Soon!</h3>
            <p>
              If you're seeing this, it means the app isn't ready on this platform. BackPass will be in touch.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default InauguralConfirmationPage;